// Capa HTTP de la bandeja de Revisiones pendientes (Módulo G, RF-G1). Tipos +
// funciones fetch; el estado vive en TanStack Query
// (`src/hooks/useRevisiones.ts`), mismo patrón que `lib/informes.ts`.
//
// Archivo propio y no una sección más de `lib/informes.ts` porque es OTRA
// pantalla con otro endpoint: comparte el tipo `Informe` —la fila es un
// informe— pero no el listado, ni sus filtros, ni su orden.

import { apiFetch } from "./api";
import type { Cotizacion, EjecutivoDelInforme, Informe } from "./informes";

/** Quién ocupa uno de los dos roles de revisor. `null` = sin asignar. */
export interface RevisorDelInforme {
  id: number;
  nombre: string;
}

/**
 * Fila de la bandeja: el informe con los tres responsables que la pantalla
 * muestra y la cotización que está esperando visado.
 *
 * `cotizacion` se estrecha a NO opcional: en esta bandeja siempre viaja
 * —`Informe::cotizar()` la crea junto con el cambio de estado—, así que la
 * pantalla no tiene que preguntarse si la clave existe. Sigue pudiendo ser
 * `null` por si alguna vez se escribiera el estado por fuera del modelo.
 */
export interface RevisionPendiente extends Informe {
  ejecutivo: EjecutivoDelInforme | null;
  revisor_cotizacion: RevisorDelInforme | null;
  revisor_informe: RevisorDelInforme | null;
  cotizacion: Cotizacion | null;
}

export interface FiltrosDeRevisiones {
  revisorId?: number;
  pagina?: number;
}

export interface PaginaDeRevisiones {
  revisiones: RevisionPendiente[];
  /** Cuántas quedaron DESPUÉS del filtro: el "de M" del pie. */
  total: number;
  /** Cuántas hay en total sin filtrar: el "N revisiones pendientes" de arriba. */
  totalGeneral: number;
  pagina: number;
  porPagina: number;
  totalPaginas: number;
}

interface RespuestaPaginada {
  data: RevisionPendiente[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    total_general: number;
  };
}

export async function fetchRevisionesPendientes(
  filtros: FiltrosDeRevisiones = {},
): Promise<PaginaDeRevisiones> {
  const parametros = new URLSearchParams();
  // Solo lo que el usuario eligió: mandar `revisor_id=` vacío haría que el
  // backend lo tome por presente y lo valide contra `users`.
  if (filtros.revisorId) parametros.set("revisor_id", String(filtros.revisorId));
  if (filtros.pagina && filtros.pagina > 1) parametros.set("page", String(filtros.pagina));

  const cadena = parametros.toString();
  const respuesta = await apiFetch<RespuestaPaginada>(
    `/api/revisiones-pendientes${cadena ? `?${cadena}` : ""}`,
  );

  return {
    revisiones: respuesta.data,
    total: respuesta.meta.total,
    totalGeneral: respuesta.meta.total_general,
    pagina: respuesta.meta.current_page,
    porPagina: respuesta.meta.per_page,
    totalPaginas: respuesta.meta.last_page,
  };
}

/**
 * Las personas que el filtro ofrece: quienes HOY ocupan un rol de revisor en
 * algún informe.
 *
 * Endpoint propio y no `/informes/catalogos`: aquel devuelve TODOS los
 * usuarios asignables, y ofrecer como filtro a alguien que nunca fue revisor
 * es prometer una respuesta que la bandeja no tiene.
 */
export async function fetchRevisoresDeInformes(): Promise<RevisorDelInforme[]> {
  const respuesta = await apiFetch<{ data: RevisorDelInforme[] }>(
    "/api/revisiones-pendientes/revisores",
  );
  return respuesta.data;
}

/**
 * El revisor aprueba la cotización vigente y queda enviada al cliente (RF-G3).
 *
 * EL CUERPO NO TOCA LA OFERTA: el precio y el plazo son los que el revisor
 * acaba de mirar y este endpoint no puede cambiarlos. Lo único que acepta son
 * las direcciones en COPIA del correo al cliente, que es una decisión del
 * despacho y no del presupuesto. Sin copias —el caso normal— sigue siendo un
 * POST sin cuerpo.
 *
 * Es un POST y no un PUT porque no reemplaza un recurso: registra un hecho, el
 * visado.
 *
 * `cotizacion` en SINGULAR en la URL: lo que se aprueba es la que está en pie,
 * no una versión elegida por el llamador. `/cotizaciones` en plural es la
 * cadena de versiones, y ahí es donde se crea una nueva.
 */
export async function aprobarCotizacionRequest(
  informeId: number,
  copias: string[] = [],
): Promise<Cotizacion> {
  const respuesta = await apiFetch<{ data: Cotizacion }>(
    `/api/informes/${informeId}/cotizacion/aprobar`,
    // La clave se OMITE cuando no hay copias en vez de mandar `cc: []`: el
    // request la valida con `sometimes`, y así el envío corriente sigue siendo
    // exactamente el mismo que antes de que existiera el campo.
    copias.length > 0
      ? { method: "POST", body: JSON.stringify({ cc: copias }) }
      : { method: "POST" },
  );
  return respuesta.data;
}
